import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { API_CONFIG } from '../config/api.config';

export interface ContactoInfo {
  userid: string | null;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
}

export interface InformacionFactura {
  nombre: string;
  direccion: string;
  telefono: string;
  email: string;
  identificacion: string;
  tipo_identificacion: {
    codigo: string;
    descripcion: string;
  };
}

export interface PrecioTipoTicket {
  precio_cargo_servicio: number;
  createdAt: string;
  iva_ticket: number;
  precio_ticket_final: number;
  nombre_precio: string;
  precio_total: number;
  id: string;
  iva_cargo_servicio: number;
  nombre_moneda: string;
  precio_base: number;
  precio_cargo_servicio_final: number;
  precio_servicio_administrativo?: number;
  iva_servicio_administrativo?: number;
  precio_servicio_administrativo_final?: number;
  updatedAt: string;
  canal?: string;
  tipoprecio?: string;
}

export interface TicketItem {
  ticketid: string;
  tipoticketid: string;
  estado: string;
  precio_pagado: number;
  precioTipoTicket: PrecioTipoTicket;
}

export interface TotalesFactura {
  subtotal_tickets: number;
  porcentaje_iva_tickets: number;
  total_iva_tickets: number;
  total_tickets: number;
  subtotal_cargo_servicios: number;
  porcentaje_iva_cargo_servicios: number;
  total_iva_cargo_servicios: number;
  total_cargo_servicios: number;
  total_factura: number;
  porcentaje_pasarela_pago: number;
  total_pasarela_pago: number;
  total_general_factura: number;
}

export interface PasarelaPago {
  eventometodopagoid: string;
  nombre: string;
  comision: number;
  pasarelaData: any;
}

export interface EventoInfo {
  eventoid: string;
  nombre: string;
  lugar: string;
  url_slug: string;
  estado: string;
  descripcion?: string;
}

export interface TicketDetalle {
  CreatedAt: string;
  ItemType: string;
  PK: string;
  UpdatedAt: string;
  cartid: string | null;
  estado: string;
  eventoid: string;
  facturaid: string;
  fecha_reserva: string;
  fecha_validacion: string | null;
  fecha_venta: string;
  grupoid: string;
  precioTipoTicket: PrecioTipoTicket;
  precio_pagado: number;
  qr: string;
  secuencial: string;
  ticketid: string;
  tipoticketid: string;
  userid: string;
  SK: string;
  tipoprecio: string;
  tipoticket?: {
    tipoticketid: string;
    nombre_localidad: string;
    eventoid: string;
    [key: string]: any;
  } | null;
  funcion?: {
    eventofuncionid: string;
    tipoticketid: string;
    subeventoid?: string | null;
    fecha_funcion?: string;
    subevento?: {
      subeventoid: string;
      nombre: string;
      [key: string]: any;
    } | null;
    [key: string]: any;
  } | null;
}

export interface EmailStatusHistory {
  status: string;
  timestamp: string;
  reason?: string | null;
  sg_event_id?: string;
  response?: string | null;
}

export interface EmailMetrics {
  delivered: boolean;
  opened: boolean;
  clicked: boolean;
  open_count: number;
  click_count: number;
  open_rate: number;
  click_rate: number;
}

export interface EmailLog {
  emaillogid: string;
  userid: string;
  facturaid: string;
  eventoid: string;
  sg_message_id: string;
  sg_event_id: string;
  to_email: string;
  to_name: string;
  from_email: string;
  from_name: string;
  subject: string;
  email_type: string;
  template_id: string | null;
  status: string;
  status_history: EmailStatusHistory[];
  error_message: string | null;
  bounce_reason: string | null;
  bounce_type: string | null;
  sent_at: string;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  failed_at: string | null;
  open_count: number;
  click_count: number;
  metrics: EmailMetrics;
  createdAt: string;
  updatedAt: string;
}

export interface Factura {
  facturaid: string;
  userid: string;
  eventoid: string;
  fecha_emision: string;
  contacto: ContactoInfo;
  informacionfactura: InformacionFactura;
  items: TicketItem[];
  totales: TotalesFactura;
  pasarela_pago: PasarelaPago;
  estado: string;
  createdAt: string;
  updatedAt: string;
  evento: EventoInfo;
  tickets?: TicketDetalle[];
  ticketformsdata?: TicketFormData[];
  emailLogs?: EmailLog[];
}

export interface TicketFormFieldData {
  [fieldName: string]: any;
}

export interface TicketFormData {
  ticketid: string;
  forms: {
    templateformid: string;
    formData: TicketFormFieldData;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

export interface FacturacionData {
  cartid: string;
  paymentid?: string; // ID de pago generado
  userid: string | null;
  eventoid: string;
  contacto: ContactoInfo;
  informacionfactura: InformacionFactura;
  eventometodopagoid: string;
  pasarelaData: any | null;
  payphoneToken?: string; // Token de PayPhone para autenticación
  ticketformsdata?: TicketFormData[];
}

export interface CreateInvoiceRequest {
  cartid: string;
  userid: string | null;
  eventoid: string;
  contacto: ContactoInfo;
  informacionfactura: InformacionFactura;
  eventometodopagoid: string;
  pasarelaData: any | null;
}

export interface CreateInvoiceResponse {
  success: boolean;
  message: string;
  data: {
    factura: Factura;
    newUserCreated?: boolean;
    login?: {
      accessToken: string;
      refreshToken: string;
      user: any;
    };
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class FacturacionService {
  private facturacionDataSubject = new BehaviorSubject<FacturacionData | null>(null);
  public facturacionData$ = this.facturacionDataSubject.asObservable();
  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;
  private http = inject(HttpClient);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // Intentar cargar datos guardados al iniciar (solo en el navegador)
    if (this.isBrowser) {
      this.loadFromStorage();
    }
  }

  /**
   * Crear o actualizar datos de facturación
   */
  setFacturacionData(data: FacturacionData): void {
    this.facturacionDataSubject.next(data);
    this.saveToStorage(data);
    console.log('✅ Datos de facturación guardados:', data);
  }

  /**
   * Obtener datos de facturación actuales
   */
  getFacturacionData(): FacturacionData | null {
    return this.facturacionDataSubject.value;
  }

  /**
   * Obtener datos de facturación por cartid
   */
  getFacturacionByCartId(cartid: string): FacturacionData | null {
    const data = this.getFacturacionData();
    if (data && data.cartid === cartid) {
      return data;
    }
    return null;
  }

  /**
   * Obtener datos de facturación por paymentid (legacy) o cartid (nuevo flujo)
   */
  getFacturacionByPaymentId(paymentid: string): FacturacionData | null {
    const data = this.getFacturacionData();
    if (data && (data.paymentid === paymentid || data.cartid === paymentid)) {
      return data;
    }
    return null;
  }

  /**
   * Actualizar datos de pasarela.
   * Soporta paymentid (legacy) y cartid (nuevo flujo).
   */
  updatePasarelaData(paymentid: string, pasarelaData: any): void {
    const currentData = this.getFacturacionData();
    if (currentData && (currentData.paymentid === paymentid || currentData.cartid === paymentid)) {
      currentData.pasarelaData = pasarelaData;
      this.setFacturacionData(currentData);
      console.log('✅ Datos de pasarela actualizados para identificador:', paymentid);
    } else {
      console.error('❌ No se encontraron datos de facturación para el identificador:', paymentid);
      console.log('currentData.paymentid:', currentData?.paymentid, 'currentData.cartid:', currentData?.cartid, 'vs id:', paymentid);
    }
  }

  /**
   * Limpiar datos de facturación
   */
  clearFacturacionData(): void {
    this.facturacionDataSubject.next(null);
    this.removeFromStorage();
    console.log('🧹 Datos de facturación limpiados');
  }

  /**
   * Limpiar si el cartid o sessionId cambian
   */
  clearIfCartOrSessionChanged(currentCartId: string, currentSessionId: string): void {
    const data = this.getFacturacionData();
    if (data && data.cartid !== currentCartId) {
      console.log('🔄 CartId cambió, limpiando datos de facturación');
      this.clearFacturacionData();
    }
  }

  /**
   * Guardar en localStorage
   */
  private saveToStorage(data: FacturacionData): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem('facturacion_data', JSON.stringify(data));
    } catch (error) {
      console.error('❌ Error al guardar datos de facturación:', error);
    }
  }

  /**
   * Cargar desde localStorage
   */
  private loadFromStorage(): void {
    if (!this.isBrowser) return;
    try {
      const stored = localStorage.getItem('facturacion_data');
      if (stored) {
        const data = JSON.parse(stored);
        this.facturacionDataSubject.next(data);
        console.log('📥 Datos de facturación cargados desde localStorage');
      }
    } catch (error) {
      console.error('❌ Error al cargar datos de facturación:', error);
    }
  }

  /**
   * Eliminar de localStorage
   */
  private removeFromStorage(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem('facturacion_data');
    } catch (error) {
      console.error('❌ Error al eliminar datos de facturación:', error);
    }
  }

  /**
   * Obtener todas las facturas del usuario autenticado
   */
  getMyInvoices(): Observable<ApiResponse<Factura[]>> {
    if (!this.isBrowser) {
      throw new Error('Cannot access localStorage on server');
    }

    const headers: { [key: string]: string } = {
      'x-api-key': API_CONFIG.api_key,
      'Content-Type': 'application/json'
    };

    if (!API_CONFIG.cookie_http_only) {
      const token = localStorage.getItem(API_CONFIG.storage.tokenKey);
      if (!token) {
        console.error('❌ No se encontró token de autenticación');
        throw new Error('No authentication token found');
      }

      console.log('🔑 Token encontrado para obtener facturas');
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('🍪 Usando cookie HTTP-only para obtener facturas');
    }

    return this.http.get<ApiResponse<Factura[]>>(
      `${API_CONFIG.baseUrl}/facturas/my-invoices`,
      {
        headers,
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }

  /**
   * Obtener detalle completo de una factura
   */
  getInvoiceDetails(facturaid: string): Observable<ApiResponse<Factura>> {
    if (!this.isBrowser) {
      throw new Error('Cannot access localStorage on server');
    }

    const headers: { [key: string]: string } = {
      'x-api-key': API_CONFIG.api_key,
      'Content-Type': 'application/json'
    };

    if (!API_CONFIG.cookie_http_only) {
      const token = localStorage.getItem(API_CONFIG.storage.tokenKey);
      if (!token) {
        console.error('❌ No se encontró token de autenticación');
        throw new Error('No authentication token found');
      }

      console.log('🔑 Token encontrado para obtener detalle de factura:', facturaid);
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('🍪 Usando cookie HTTP-only para obtener detalle de factura:', facturaid);
    }

    return this.http.get<ApiResponse<Factura>>(
      `${API_CONFIG.baseUrl}/facturas/${facturaid}/details`,
      {
        headers,
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }

  /**
   * Crear factura desde carrito
   * En modo cookie_http_only, el backend lee el sessionId automáticamente de la cookie
   */
  createInvoice(request: CreateInvoiceRequest): Observable<CreateInvoiceResponse> {
    return this.http.post<CreateInvoiceResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.facturas.create}`,
      request,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key,
          'Content-Type': 'application/json'
        },
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }

  /**
   * Reenviar email de factura
   */
  resendFacturaEmail(facturaid: string): Observable<ApiResponse<any>> {
    if (!this.isBrowser) {
      throw new Error('Cannot access localStorage on server');
    }

    const headers: { [key: string]: string } = {
      'x-api-key': API_CONFIG.api_key,
      'Content-Type': 'application/json'
    };

    if (!API_CONFIG.cookie_http_only) {
      const token = localStorage.getItem(API_CONFIG.storage.tokenKey);
      if (!token) {
        console.error('❌ No se encontró token de autenticación');
        throw new Error('No authentication token found');
      }

      console.log('🔑 Token encontrado para reenviar email de factura:', facturaid);
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('🍪 Usando cookie HTTP-only para reenviar email de factura:', facturaid);
    }

    const url = API_CONFIG.endpoints.facturas.resendEmail.replace('{facturaid}', facturaid);

    return this.http.post<ApiResponse<any>>(
      `${API_CONFIG.baseUrl}${url}`,
      {},
      {
        headers,
        withCredentials: API_CONFIG.cookie_http_only
      }
    );
  }
}
